<template>
  <v-container>
    <v-combobox
      :color="activeColor"
      v-model="localPlaylist.keywords"
      :items="tags.keywords"
      :rules="rules.tags"
      :search-input.sync="tagSearchString"
      :disabled="isUpdating"
      label="Keywords/Tags"
      chips
      flat
      clearable
      multiple
      ref="keywords"
      @blur="handleTagInput('keywords')"
      @click:clear="clearTags('keywords')"
      :loading="searchingForTags">
      <template slot="selection" slot-scope="{ item, index }">
        <v-chip
          :style="getTagStyles('user-generated', item)"
          close
          :disabled="isUpdating"
          @click:close="handleChipRemoval({
            index,
            type: 'keywords',
          })">
          <strong>{{ item }}</strong>
        </v-chip>
      </template>
    </v-combobox>
    <v-autocomplete
      :color="activeColor"
      class="select-overflow"
      v-model="localPlaylist.age"
      :items="tags.age"
      :disabled="isUpdating"
      item-text="value"
      label="Target Audience"
      chips
      flat
      clearable
      multiple
      ref="age"
      @blur="handleTagInput('age')"
      @click:clear="clearTags('age')">
      <template
        slot="selection"
        slot-scope="{ item, index }">
        <v-chip
          :style="getTagStyles('age', item.value)"
          close
          :disabled="isUpdating"
          @click:close="handleChipRemoval({
            index,
            type: 'age',
          })">
          <strong>
            {{ item.value }}
          </strong>&nbsp;
        </v-chip>
      </template>
    </v-autocomplete>
    <v-autocomplete
      :color="activeColor"
      class="select-overflow"
      v-model="tagType"
      :items="tags.subject"
      :disabled="isUpdating"
      item-text="value"
      label="Subject"
      chips
      flat
      clearable
      multiple
      ref="subject"
      @blur="handleTagInput('subject')"
      @click:clear="clearTags('subject')">
      <template
        slot="selection"
        slot-scope="{ item, index }">
        <v-chip
          :style="getTagStyles('subject', item.value)"
          close
          :disabled="isUpdating"
          @click:close="handleChipRemoval({
            index,
            type: 'subject',
          })">
          <strong>
            {{ item.value }}
          </strong>&nbsp;
        </v-chip>
      </template>
    </v-autocomplete>
  </v-container>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';
import _ from 'lodash';
import Color from 'color';

import bannedTags from '../js/bannedTags';

export default {
  name: 'PlaylistTags',
  props: {
    activeColor: {
      type: String,
      default: '#2196F3',
    },
    playlist: {
      type: Object,
    },
  },
  data: () => ({
    localPlaylist: {
      age: [],
      keywords: [],
      subject: [],
    },
    originalPlaylist: {},
    referencePlaylist: {},
    rules: {
      name: [
        name => !!(name && name.length) || 'This field is required.',
      ],
      tags: [
        tags => tags.every(tag => bannedTags.indexOf(tag) === -1) || 'Inappropriate tags are not allowed',
        tags => tags.every(tag => tag.length <= 255) || 'Tags must be less than 255 characters',
      ],
    },
    searchingForTags: false,
    tags: {
      age: [],
      keywords: [],
      subject: [],
    },
    tagMatchesSearched: false,
    tagSearchString: null,
    uniqueTagStyles: [
      {
        value: 'user-generated',
        hex: '#fafafa',
      },
      {
        value: 'age',
        hex: '#e6f0f4',
      },
      {
        value: 'subject',
        hex: '#e8e6f4',
      },
    ],
  }),
  computed: {
    ...mapState([
      'isUpdating',
      'playlistModalLoading',
    ]),
    tagStyles() {
      const tagStyles = {};
      for (const tag of this.uniqueTagStyles) {
        tagStyles[tag.value] = {
          backgroundColor: tag.hex,
          color: `${Color(tag.hex).darken(0.6)} !important`,
          border: `1px solid ${Color(tag.hex).darken(0.6)} !important`,
        };
      }
      return tagStyles;
    },
    tagType: {
      get() {
        return this.localPlaylist.subject;
      },
      set(newValue) {
        this.localPlaylist.subject = newValue;
      },
    },
  },
  methods: {
    ...mapMutations([
      'setIsUpdating',
      'toggleSnackbar',
    ]),
    ...mapActions([
      'savePlaylistTags',
    ]),
    applyTagSearchString: _.debounce(function debouncedApplyTagSearchString(searchString) {
      if (_.isNil(searchString)) searchString = '';
      // reset tag matches search if input is less than 4 characters
      if (searchString.length < 3) this.tagMatchesSearched = false;
      // Trigger tag matches search when input >= 3 characters and we haven't searched yet
      if (searchString.length >= 3 && !this.tagMatchesSearched) {
        this.tagMatchesSearched = true;
        this.handleGetTags({
          isSearch: true,
          searchString,
        });
      }
    }, 275),
    clearTags(type) {
      this.localPlaylist[type] = [];
      this.$refs[type].blur();
    },
    clearTagSearchString() {
      this.$set(this, 'tagSearchString', '');
    },
    async formatDataForSave(field) {
      if (JSON.stringify(_.get(this, `originalPlaylist.${field}`)) === JSON.stringify(_.get(this, `localPlaylist.${field}`))) return;
      const playlist = {
        id: this.localPlaylist.id,
        addTags: [],
        deleteTags: [],
      };
      for (const playlistTag of this.referencePlaylist.playlistTags) {
        if (
          playlistTag.tag.type === 'user-generated'
          && this.localPlaylist.keywords.indexOf(playlistTag.text) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
        if (
          playlistTag.tag.type === 'age'
          && this.localPlaylist.age.indexOf(playlistTag.tag.value) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
        if (
          playlistTag.tag.type === 'subject'
          && this.localPlaylist.subject.indexOf(playlistTag.tag.value) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
      }
      for (const keyword of this.localPlaylist.keywords) {
        if (this.originalPlaylist.keywords.indexOf(keyword.trim()) >= 0) continue; // eslint-disable-line no-continue
        playlist.addTags.push({
          playlistId: this.localPlaylist.id,
          tagId: 1,
          text: keyword.trim(),
        });
      }
      for (const ageTag of this.localPlaylist.age) {
        if (this.originalPlaylist.age.indexOf(ageTag) >= 0) continue; // eslint-disable-line no-continue
        const tag = _.find(this.tags.age, { value: ageTag });
        if (
          tag.type === 'age'
          && tag.value === ageTag
        ) {
          playlist.addTags.push({
            playlistId: this.localPlaylist.id,
            tagId: tag.id,
          });
        }
      }
      for (const subjectTag of this.localPlaylist.subject) {
        if (this.originalPlaylist.subject.indexOf(subjectTag) >= 0) continue; // eslint-disable-line no-continue
        const tag = _.find(this.tags.subject, { value: subjectTag });
        if (
          tag.type === 'subject'
          && tag.value === subjectTag
        ) {
          playlist.addTags.push({
            playlistId: this.localPlaylist.id,
            tagId: tag.id,
          });
        }
      }
      const { data: updatedPlaylistData } = await this.saveTags(playlist);
      if (updatedPlaylistData) {
        this.referencePlaylist.playlistTags = [
          ...(
            _.get(updatedPlaylistData, 'playlistTags')
          ),
        ];
      }
      await this.$set(this, 'originalPlaylist', _.cloneDeep(this.localPlaylist));
    },
    getTagStyles(key, item) {
      if (
        bannedTags.includes(item.toLowerCase())
        || item.length >= 255
      ) {
        return {
          backgroundColor: '#EF5350',
          color: 'white !important',
          border: '1px solid #602120 !important',
        };
      }
      return this.tagStyles[key];
    },
    async handleChipRemoval({
      index,
      type,
    } = {}) {
      if (type === 'keywords') this.localPlaylist.keywords.splice(index, 1);
      else this.$delete(this.localPlaylist[type], index);
      this.$refs[type].blur();
    },
    async handleGetTags({
      searchString = undefined,
      isSearch = true,
    } = {}) {
      try {
        if (this.searchingForTags) return;
        this.searchingForTags = true;
        // We are hitting a Dash route - hence the data.data shenanigans
        const { data: { data: tagData } } = await this.$api.playlist.getTags({
          isSearch,
          searchString,
        });
        for (const tag of tagData) {
          if (tag.type === 'age') {
            this.tags.age.push({
              hex: tag.hex,
              id: tag.id,
              value: tag.value,
              type: tag.type,
            });
          }
          if (tag.type === 'subject') {
            this.tags.subject.push({
              hex: tag.hex,
              id: tag.id,
              value: tag.value,
              type: tag.type,
            });
          }
          if (tag.id === 1) {
            tag.value.forEach(word => this.tags.keywords.push(word.text));
          }
        }
      } catch (e) {
        this.toggleSnackbar({
          text: 'Error searching for tags.',
          color: 'error',
          timeout: 5000,
        });
      } finally {
        this.searchingForTags = false;
      }
    },
    handleTagInput(field) {
      this.formatDataForSave(field);
      this.$set(this, 'tagSearchString', '');
    },
    initPlaylist() {
      this.$set(this, 'referencePlaylist', _.cloneDeep(this.playlist));
      // eslint-disable-next-line guard-for-in
      for (const key in this.referencePlaylist) {
        this.localPlaylist[key] = this.referencePlaylist[key];
        if (key === 'playlistTags') {
          for (const playlistTag of this.referencePlaylist[key]) {
            if (
              playlistTag.tag.type === 'user-generated'
              && !this.localPlaylist.keywords.includes(playlistTag.text)
            ) this.localPlaylist.keywords.push(playlistTag.text);
            if (
              playlistTag.tag.type === 'age'
              && !this.localPlaylist.age.includes(playlistTag.tag.value)
            ) this.localPlaylist.age.push(playlistTag.tag.value);
            if (
              playlistTag.tag.type === 'subject'
              && !this.localPlaylist.subject.includes(playlistTag.tag.value)
            ) this.localPlaylist.subject.push(playlistTag.tag.value);
          }
        }
      }
      this.handleGetTags({
        isSearch: true,
      });
      this.$set(this, 'originalPlaylist', _.cloneDeep(this.localPlaylist));
    },
    async saveTags(playlist) {
      try {
        this.setIsUpdating(true);
        const updatedPlaylist = await this.$api.playlist.saveTags({
          addTags: playlist.addTags,
          deleteTags: playlist.deleteTags,
          playlistId: playlist.id,
        });
        this.toggleSnackbar({
          text: 'Tags updated!',
          color: 'success',
          timeout: 6000,
        });
        return updatedPlaylist;
      } catch (e) {
        this.toggleSnackbar({
          text: 'Error updating tags.',
          color: 'warning',
          timeout: 6000,
        });
        return false;
      } finally {
        this.setIsUpdating(false);
      }
    },
  },
  watch: {
    tagSearchString(n) {
      this.applyTagSearchString(n);
    },
  },
  created() {
    this.initPlaylist();
  },
};
</script>
